import aiAgents from "./AIAgents";

class VoiceSynthesizer {
  constructor() {
    if (VoiceSynthesizer.instance) {
      return VoiceSynthesizer.instance;
    }
    VoiceSynthesizer.instance = this;
    this.queue = [];
    this.isSpeaking = false;
    this.agent = null;
    this.voice = null;
    this.resumeInterval = null; // Interval reference
    this.amplitudeAnimationId = null; // Animation frame ID for amplitude
    window.voiceSynthesizer = this;
    this.voicesLoaded = false;
  }

  loadVoices() {
    if (!window.speechSynthesis) {
      console.error("Speech synthesis is not supported in this browser.");
      return;
    }

    // Listen for voiceschanged event
    window.speechSynthesis.onvoiceschanged = () => {
      this.voice = this.getVoice();
    };

    // Try to get voices immediately
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      this.voice = this.getVoice();
    }

    this.voicesLoaded = true;
  }

  isFirefox() {
    return typeof InstallTrigger !== "undefined"; // A quick way to detect Firefox
  }

  getVoice() {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) {
      console.warn("getVoice called but voices array is empty or not yet loaded.");
      return null; // Wait for onvoiceschanged
    }

    // List of names often associated with female voices (lowercase) - keep this updated
    const knownFemaleNames = [
      "google us english",
      "nicky",
      "catherine",
      "karen",
      "martha",
      "moira",
      "samantha",
      "sandy",
      "shelley",
      "tessa",
      "natasha",
      "clara",
      "neerja",
      "emily",
      "asilia",
      "molly",
      "ezinne",
      "rosa",
      "ava",
      "emma",
      "luna",
      "leah",
      "imani",
      "libby",
      "maisie",
      "sonia",
      "ana",
      "aria",
      "jenny",
      "michelle",
      "zira",
      "victoria",
      "fiona",
      "eva",
      "hazel",
      "susan"
    ];

    // Helper function to check if a voice name matches known female names
    const isKnownFemale = voice => {
      if (!voice || !voice.name) return false;
      const voiceNameLower = voice.name.toLowerCase();
      return knownFemaleNames.some(femaleName => voiceNameLower.includes(femaleName));
    };

    let selectedVoice = null;

    selectedVoice = voices.find(v => v.localService && v.lang.toLowerCase() === "en-us" && isKnownFemale(v));
    if (selectedVoice) {
      return selectedVoice;
    }

    selectedVoice = voices.find(
      v =>
        v.localService && v.lang.toLowerCase().startsWith("en") && v.lang.toLowerCase() !== "en-us" && isKnownFemale(v)
    );
    if (selectedVoice) {
      return selectedVoice;
    }

    // 3. Local US English (Any Gender)
    selectedVoice = voices.find(v => v.localService && v.lang.toLowerCase() === "en-us");
    if (selectedVoice) {
      return selectedVoice;
    }

    // 4. Local Other English (Any Gender)
    selectedVoice = voices.find(
      v => v.localService && v.lang.toLowerCase().startsWith("en") && v.lang.toLowerCase() !== "en-us"
    );
    if (selectedVoice) {
      return selectedVoice;
    }

    console.warn("No suitable local English voice found. Falling back to non-local voices.");

    selectedVoice = voices.find(v => !v.localService && v.lang.toLowerCase() === "en-us" && isKnownFemale(v));
    if (selectedVoice) {
      return selectedVoice;
    }

    // 6. Non-Local Other English Female
    selectedVoice = voices.find(
      v =>
        !v.localService && v.lang.toLowerCase().startsWith("en") && v.lang.toLowerCase() !== "en-us" && isKnownFemale(v)
    );
    if (selectedVoice) {
      return selectedVoice;
    }

    // 7. Non-Local US English (Any Gender)
    selectedVoice = voices.find(v => !v.localService && v.lang.toLowerCase() === "en-us");
    if (selectedVoice) {
      return selectedVoice;
    }

    // 8. Non-Local Other English (Any Gender)
    selectedVoice = voices.find(
      v => !v.localService && v.lang.toLowerCase().startsWith("en") && v.lang.toLowerCase() !== "en-us"
    );
    if (selectedVoice) {
      return selectedVoice; // This might be the original slow Google UK Female if others weren't found
    }

    // --- ABSOLUTE FALLBACK ---
    // If NO English voice was found at all (highly unlikely but possible)
    const anyEnglishVoice = voices.find(v => v.lang.toLowerCase().startsWith("en"));
    if (anyEnglishVoice) {
      console.error(
        "Could not find any preferred English voice based on criteria. Selecting first available English voice:",
        anyEnglishVoice.name
      );
      return anyEnglishVoice;
    }

    // If truly desperate, return the very first voice regardless of language
    console.error(
      "Could not find *any* English voice. Selecting first available voice overall:",
      voices.length > 0 ? voices[0].name : "None available"
    );
    return voices.length > 0 ? voices[0] : null;
  }

  // Start simulating amplitude for Web Speech API (since we can't get real amplitude)
  startAmplitudeSimulation() {
    if (this.amplitudeAnimationId || !this.agent) {
      return;
    }

    let lastUpdateTime = 0;
    const updateInterval = 100; // Update every 100ms like OpenAI

    const amplitudeLoop = (currentTime) => {
      if (currentTime - lastUpdateTime >= updateInterval) {
        if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
          // Simulate realistic amplitude patterns like real OpenAI audio
          const random = Math.random();
          let amplitude;

          // 15% chance of silence (0 values)
          if (random < 0.15) {
            amplitude = 0;
          }
          // 20% chance of low background activity
          else if (random < 0.35) {
            amplitude = Math.random() * 0.02; // 0 to 0.02
          }
          // 40% chance of moderate activity
          else if (random < 0.75) {
            amplitude = 0.02 + Math.random() * 0.08; // 0.02 to 0.10
          }
          // 25% chance of strong peaks
          else {
            amplitude = 0.1 + Math.random() * 0.3; // 0.1 to 0.4
          }

          aiAgents.dispatchAgentTalking(this.agent, amplitude);
        }
        lastUpdateTime = currentTime;
      }

      this.amplitudeAnimationId = requestAnimationFrame(amplitudeLoop);
    };

    this.amplitudeAnimationId = requestAnimationFrame(amplitudeLoop);
  }

  // Stop amplitude simulation
  stopAmplitudeSimulation() {
    if (this.amplitudeAnimationId) {
      cancelAnimationFrame(this.amplitudeAnimationId);
      this.amplitudeAnimationId = null;
    }
  }

  splitText(text, maxChunkSize = 200) {
    const sentences = text.match(/[^.!?]+[.!?]+|\s*\S+$/g) || [text];
    const chunks = [];
    let currentChunk = "";

    sentences.forEach(sentence => {
      if ((currentChunk + sentence).length <= maxChunkSize) {
        currentChunk += sentence;
      } else {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      }
    });

    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  }

  // Method to add text to the synthesis queue with optional context
  addToQueue(text, split = false, messageId, groupId) {
    let chunks = [];
    if (split) {
      chunks = this.splitText(text);
    } else {
      chunks = [text];
    }

    // Push objects with context into the queue
    chunks.forEach(chunkText => {
      this.queue.push({
        text: chunkText,
        messageId,
        groupId
      });
    });

    if (!this.isSpeaking) {
      this.processQueue();
    }
  }

  // Process the next item in the queue
  processQueue() {
    if (this.queue.length === 0) {
      this.isSpeaking = false;
      aiAgents.handleFinishEndAudio(this.agent);
      return;
    }

    this.isSpeaking = true;
    const currentItem = this.queue.shift();
    this.speakChunk(currentItem);
  }

  // Speak a single chunk of text with context
  speakChunk(item) {
    if (!window.speechSynthesis) {
      console.error("Speech synthesis is not supported in this browser.");
      return;
    }

    const utterance = new SpeechSynthesisUtterance(item.text);
    this.utterance = utterance;

    if (this.voice) {
      utterance.voice = this.voice;
    }

    // Customize pitch, rate, and volume
    utterance.pitch = 1.2;
    utterance.rate = 1;
    utterance.volume = 1;

    // Listen for the start of speech
    utterance.onstart = () => {
      aiAgents.dispatchStartSpeaking(this.agent);
      this.startAmplitudeSimulation(); // Start amplitude simulation

      // Dispatch animation event when speech actually starts
      if (item.messageId !== undefined && item.groupId !== undefined) {
        window.APP.messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message-edit", {
            detail: {
              type: "private-chat",
              body: { play: true, loadingAudio: false },
              messageId: item.messageId,
              groupId: item.groupId
            }
          })
        );
      }
    };

    // Listen for the end of speech to process the next chunk
    utterance.onend = () => {
      clearInterval(this.resumeInterval);
      this.stopAmplitudeSimulation(); // Stop amplitude simulation
      this.processQueue();
    };

    // Listen for errors
    utterance.onerror = e => {
      if (e.error !== "interrupted") {
        console.warn("Error in speech synthesis:", e);
      }
      clearInterval(this.resumeInterval); // Clear the interval on error
      this.processQueue();
    };

    // Use setInterval to resume if necessary
    this.resumeInterval = setInterval(() => {
      if (!window.speechSynthesis.speaking) {
        clearInterval(this.resumeInterval);
      } else {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 13000); // Resume every 14 seconds

    // Speak the text
    window.speechSynthesis.speak(utterance);
  }

  speakPrompt(prompt) {
    if (!window.speechSynthesis) {
      console.error("Speech synthesis is not supported in this browser.");
      return;
    }

    const utterance = new SpeechSynthesisUtterance(prompt);
    this.utterance = utterance;

    // Use the pre-selected voice
    if (this.voice) {
      utterance.voice = this.voice;
    }

    // Customize pitch, rate, and volume
    utterance.pitch = 1.2;
    utterance.rate = 1;
    utterance.volume = 1;

    // Listen for the start of speech
    utterance.onstart = () => {
      aiAgents.dispatchStartSpeaking(this.agent);
    };

    // Listen for the end of speech to process the next chunk
    utterance.onend = () => {
      clearInterval(this.resumeInterval);
      setTimeout(() => {
        aiAgents.handleFinishEndAudio(this.agent);
      }, 2000);
    };

    // Listen for errors
    utterance.onerror = e => {
      console.error("Error in speech synthesis:", e);
      clearInterval(this.resumeInterval); // Clear the interval on error
      setTimeout(() => {
        aiAgents.handleFinishEndAudio(this.agent);
      }, 2000);
    };

    // Use setInterval to resume if necessary
    this.resumeInterval = setInterval(() => {
      if (!window.speechSynthesis.speaking) {
        clearInterval(this.resumeInterval);
      } else {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 10000); // Resume every 14 seconds

    // Speak the text
    window.speechSynthesis.speak(utterance);
  }

  stop() {
    window.speechSynthesis.cancel();
  }

  messageIsQueued(messageId) {
    return this.queue.some(item => item.messageId === messageId);
  }
}

// Exporting a singleton instance
const voiceSynthesizer = new VoiceSynthesizer();

export default voiceSynthesizer;
